import { Component, computed, effect, inject, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { UserController, UserEntity } from '@domain';
import { Subscription, take } from 'rxjs';
import { RaLoadingComponent, RaUserAvatarComponent } from '@shared/components';
import { RaSwitchComponent } from 'ra-ui';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditService } from '../../../services/edit-service';

/**
 * Modal de gestão da equipe do documento (responsáveis). Mesmo layout/comportamento
 * do modal "Equipe do serviço": uma lista única de TODA a equipe da empresa, com um
 * switch por membro que SALVA NA HORA (auto-save) — ligar adiciona como responsável,
 * desligar remove. Qualquer usuário pode gerenciar a equipe (não há gate de papel).
 */
@Component({
    selector: 'modal-responsibles',
    templateUrl: 'modal-responsibles.component.html',
    styleUrl: 'modal-responsibles.component.scss',
    imports: [
        RaModalComponent,
        RaLoadingComponent,
        RaEmptyComponent,
        RaSwitchComponent,
        RaUserAvatarComponent,
        RaIconComponent,
        ReactiveFormsModule,
        TranslatePipe,
    ],
})
export class ModalResponsiblesComponent implements OnInit, OnDestroy {
    public close = output<void>();

    public loading = signal(false);
    public members = signal<UserEntity[]>([]);

    /** Membros com salvamento em andamento (switch travado até responder). */
    public savingIds = signal<Set<string>>(new Set());

    // FormControl (switch) de cada membro
    public memberControlsMap = signal<Map<string, FormControl>>(new Map());

    // Estado inicial de cada membro (se já era responsável)
    private _initialStateMap = new Map<string, boolean>();

    // Subscriptions dos FormControls
    private _controlSubscriptions = new Map<string, Subscription>();

    // * [Injects]
    public editDataService = inject(EditDataService);
    private _editService = inject(EditService);
    private _userController = inject(UserController);

    /** Usuário logado — ganha o marcador `você` na lista. */
    public currentUserId = '';

    /** IDs (normalizados) dos responsáveis atuais do documento. */
    private _responsibleIds = computed(() => {
        const ids = new Set<string>();
        (this.editDataService.document()?.responsibles ?? []).forEach(r => {
            const id = this._normalizeId(r.userId);
            if (id) ids.add(id);
        });
        return ids;
    });

    constructor() {
        // Reage a mudanças nos responsáveis (ex.: save concluído) e sincroniza os switches.
        effect(() => {
            const respIds = this._responsibleIds();
            const members = this.members();
            const controlsMap = this.memberControlsMap();

            if (members.length > 0 && controlsMap.size === 0) {
                this._initializeMemberControls(members, respIds);
            } else if (members.length > 0 && controlsMap.size > 0) {
                this._updateMemberControls(members, respIds);
            }
        });
    }

    ngOnInit(): void {
        this.currentUserId = this._userController.getCurrentUserId();
        this._getMembers();
    }

    private _getMembers(): void {
        this.loading.set(true);
        // Equipe COMPLETA, incluindo o usuário logado (o `getEmployees` o exclui).
        this._userController
            .getAllTeam()
            .pipe(take(1))
            .subscribe({
                next: members => {
                    this.members.set(members ?? []);
                    if (this.memberControlsMap().size === 0) {
                        this._initializeMemberControls(members ?? [], this._responsibleIds());
                    }
                    this.loading.set(false);
                },
                error: () => this.loading.set(false),
            });
    }

    private _normalizeId(id: unknown): string {
        if (!id) return '';
        if (typeof id === 'object' && (id as { toString?: () => string }).toString) {
            return String((id as { toString: () => string }).toString());
        }
        return String(id);
    }

    private _initializeMemberControls(members: UserEntity[], respIds: Set<string>): void {
        const controlsMap = new Map<string, FormControl>();

        members.forEach(member => {
            const memberId = this._normalizeId(member._id);
            const isSelected = respIds.has(memberId);
            const control = new FormControl(isSelected);

            this._initialStateMap.set(memberId, isSelected);
            this._subscribeToControlChanges(memberId, control);
            controlsMap.set(memberId, control);
        });

        this.memberControlsMap.set(controlsMap);
    }

    private _updateMemberControls(members: UserEntity[], respIds: Set<string>): void {
        const controlsMap = this.memberControlsMap();

        members.forEach(member => {
            const memberId = this._normalizeId(member._id);
            const control = controlsMap.get(memberId);

            if (control) {
                const shouldBeSelected = respIds.has(memberId);
                // Só atualiza se diferente para evitar loop de eventos.
                if (control.value !== shouldBeSelected) {
                    control.setValue(shouldBeSelected, { emitEvent: false });
                }
            }
        });
    }

    public getMemberControl(memberId: string): FormControl {
        const normalizedId = String(memberId);
        const control = this.memberControlsMap().get(normalizedId);

        if (!control) {
            const newControl = new FormControl(false);
            this._subscribeToControlChanges(normalizedId, newControl);
            return newControl;
        }

        return control;
    }

    private _subscribeToControlChanges(memberId: string, control: FormControl): void {
        this._controlSubscriptions.get(memberId)?.unsubscribe();

        const subscription = control.valueChanges.subscribe(newValue => {
            this._handleMemberToggle(memberId, !!newValue);
        });

        this._controlSubscriptions.set(memberId, subscription);
    }

    /**
     * SALVA NA HORA (auto-save): ligar adiciona o responsável, desligar remove.
     * O switch fica travado durante a requisição e, em caso de erro, volta ao
     * estado anterior (o toast de erro vem do controller).
     */
    private _handleMemberToggle(memberId: string, isActive: boolean): void {
        const initialState = this._initialStateMap.get(memberId) ?? false;
        if (initialState === isActive) return;

        this.savingIds.update(ids => new Set(ids).add(memberId));

        const request$ = isActive
            ? this._editService.addResponsible(memberId)
            : this._editService.removeResponsible(memberId);

        request$.pipe(take(1)).subscribe({
            next: () => {
                this._initialStateMap.set(memberId, isActive);
                this._removeSaving(memberId);
            },
            error: () => {
                // Reverte o switch sem re-disparar o save
                this.memberControlsMap()
                    .get(memberId)
                    ?.setValue(initialState, { emitEvent: false });
                this._removeSaving(memberId);
            },
        });
    }

    private _removeSaving(memberId: string): void {
        this.savingIds.update(ids => {
            const next = new Set(ids);
            next.delete(memberId);
            return next;
        });
    }

    ngOnDestroy(): void {
        this._controlSubscriptions.forEach(subscription => subscription.unsubscribe());
        this._controlSubscriptions.clear();
    }
}
